import type { DocumentType } from '../../types/tuition-assistance.ts';

// Upload rows and the submission gate must share bucket membership so every required file is actionable.

/**
 * The subset of TasAsset the matching reads. Structural so the wizard can pass
 * live records and unit tests can pass plain objects.
 */
export interface CategorizedUploadAssetLike {
  attachmentDeletedAt: string | null;
  classification: string | null;
  uploadRowKey: string | null;
  employeeSelectedType: DocumentType | null;
}

/** One admin-configured attachment row (from `documentEntriesFor`). */
export interface DocumentEntryLike {
  label: string;
  type: DocumentType;
}

/** Live = still attached (not discarded) and filed under this step's classification. */
export function isLiveForClassification(
  asset: CategorizedUploadAssetLike,
  classification: string
): boolean {
  return asset.attachmentDeletedAt === null && asset.classification === classification;
}

/**
 * Row-display membership. An asset tagged with an upload row key belongs only
 * to the row it was filed under; untagged (legacy) assets match by declared
 * type. Callers filter for liveness separately (the discarded tray needs
 * non-live assets).
 */
export function assetMatchesDocumentEntry(
  asset: CategorizedUploadAssetLike,
  entry: DocumentEntryLike
): boolean {
  if (asset.uploadRowKey) return asset.uploadRowKey === entry.label;
  return asset.employeeSelectedType === entry.type;
}

/**
 * True for a live upload in this classification with neither row key nor declared type. This
 * carryover escape lets long-lived applications keep evidence when templates add document rows.
 */
export function hasUntaggedLegacyUpload(
  assets: readonly CategorizedUploadAssetLike[],
  classification: string
): boolean {
  return assets.some(
    (asset) =>
      isLiveForClassification(asset, classification) &&
      !asset.uploadRowKey &&
      !asset.employeeSelectedType
  );
}

/**
 * Returns live uploads in this classification that no categorized row can display, for rendering
 * the carryover population.
 */
export function untaggedLegacyUploads<T extends CategorizedUploadAssetLike>(
  assets: readonly T[],
  classification: string
): T[] {
  return assets.filter(
    (asset) =>
      isLiveForClassification(asset, classification) &&
      !asset.uploadRowKey &&
      !asset.employeeSelectedType
  );
}

/**
 * True when every configured row has a live upload; empty entry lists are satisfied. Callers decide
 * whether an unconfigured step needs a legacy presence check.
 * Uses the display predicate: multiple rows may share a document type, and type-only assets can
 * satisfy every row of that type. Assets referencing renamed rows additionally fall back to their
 * declared type so in-flight applications can submit.
 * AI verdicts are advisory and never gate submission; persisted pending tags may have no live
 * session to resolve them.
 */
export function hasRequiredCategorizedUploads(
  assets: readonly CategorizedUploadAssetLike[],
  classification: string,
  entries: readonly DocumentEntryLike[]
): boolean {
  const configuredLabels = new Set(entries.map((entry) => entry.label));
  const live = assets.filter((asset) => isLiveForClassification(asset, classification));
  // Filed under a row the template no longer defines — see the rename carve-out.
  const orphanedByRename = live.filter(
    (asset) => asset.uploadRowKey && !configuredLabels.has(asset.uploadRowKey)
  );

  return entries.every(
    (entry) =>
      live.some((asset) => assetMatchesDocumentEntry(asset, entry)) ||
      orphanedByRename.some((asset) => asset.employeeSelectedType === entry.type)
  );
}
